// src/components/SearchModule.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Filter, Download, Users, MapPin, ChevronDown, ShoppingCart, X, ArrowUp, Image as ImageIcon } from 'lucide-react';
// X ya está importado
import * as XLSX from 'xlsx';
import apiService from '../services/apiService';
import { debugLog, errorLog } from '../config/featureFlags';

const SearchModule = () => {
  const [results, setResults] = useState([]);
  const [trips, setTrips] = useState([]);
  const [envivos, setEnvivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    tripId: '',
    clientName: '',
    envivoId: '',
    status: '',
    startDate: '',
    endDate: ''
  });
  const [sortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  const [tooltipData, setTooltipData] = useState(null);
  // const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipTimeout, setTooltipTimeout] = useState(null);
  
  // Estados para tooltip de pagos
  const [paymentTooltipData, setPaymentTooltipData] = useState(null);
  // const [paymentTooltipPosition, setPaymentTooltipPosition] = useState({ x: 0, y: 0 });
  const [paymentTooltipTimeout, setPaymentTooltipTimeout] = useState(null);
  
  // Estado para botón "Volver arriba" y control de scroll
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(true);
  const [mobileSearchTerm, setMobileSearchTerm] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [quickCheckUpdatingId, setQuickCheckUpdatingId] = useState(null);

  // Estado para controlar acordeones de resultados abiertos/cerrados
  const [openAccordions, setOpenAccordions] = useState(new Set());

  // Estados para modal de comprobante de pago
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceiptUrl, setSelectedReceiptUrl] = useState(null);
  const [receiptImageSrc, setReceiptImageSrc] = useState(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState(null);

  // Cargar viajes al montar el componente
  useEffect(() => {
    loadTrips();
  }, []);

  // Cargar en vivos cuando cambie el viaje seleccionado
  useEffect(() => {
    if (filters.tripId) {
      loadEnvivos(filters.tripId);
    } else {
      // Si no hay viaje seleccionado, cargar todos los en vivos
      loadEnvivos();
    }
  }, [filters.tripId]);

  // Cleanup del timeout al desmontar
  useEffect(() => {
    return () => {
      if (tooltipTimeout) {
        clearTimeout(tooltipTimeout);
      }
      if (paymentTooltipTimeout) {
        clearTimeout(paymentTooltipTimeout);
      }
    };
  }, [tooltipTimeout, paymentTooltipTimeout]);
  
  // Mostrar/ocultar botón "Volver arriba" y buscador móvil según scroll
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const isMobileOrTablet = window.innerWidth < 1024; // Mobile y tablet/iPad
      
      // Umbral para mostrar el botón y buscador: 200px desde el inicio
      const SHOW_THRESHOLD = 200;
      
      if (currentScrollY > SHOW_THRESHOLD) {
        setShowScrollTop(true);
        if (isMobileOrTablet) {
          setShowMobileSearch(true);
        } else {
          setShowMobileSearch(true);
        }
      } else {
        setShowScrollTop(false);
        if (isMobileOrTablet) {
          setShowMobileSearch(false);
        } else {
          setShowMobileSearch(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Función para limpiar resaltados anteriores
  const clearHighlights = useCallback(() => {
    const containers = document.querySelectorAll('.search-highlight-container');
    containers.forEach(container => {
      const text = container.textContent;
      const textNode = document.createTextNode(text);
      if (container.parentNode) {
        container.parentNode.replaceChild(textNode, container);
      }
    });
    document.body.normalize();
  }, []);

  // Función para resaltar texto en la página
  const highlightText = useCallback((term) => {
    if (!term || typeof window === 'undefined') return;
    
    clearHighlights();

    let windowFound = false;
    try {
      windowFound = window.find(term, false, false, true, false, true, false);
    } catch (e) {
      // Ignorar errores
    }

    if (!windowFound) {
      const allText = (document.body.innerText || document.body.textContent || '').toLowerCase();
      if (!allText.includes(term.toLowerCase())) {
        alert(`No se encontraron coincidencias para "${term}".`);
        return;
      }
    }

    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode: (node) => {
          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          
          if (
            parent.tagName === 'INPUT' || 
            parent.tagName === 'TEXTAREA' || 
            parent.tagName === 'SCRIPT' || 
            parent.tagName === 'STYLE' ||
            parent.closest('.search-highlight-container') ||
            parent.closest('nav') ||
            parent.closest('button')
          ) {
            return NodeFilter.FILTER_REJECT;
          }
          
          if (node.textContent.trim().length === 0) {
            return NodeFilter.FILTER_REJECT;
          }
          
          return NodeFilter.FILTER_ACCEPT;
        }
      },
      false
    );

    const textNodes = [];
    let node;
    // eslint-disable-next-line no-cond-assign
    while ((node = walker.nextNode())) {
      textNodes.push(node);
    }

    const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedTerm})`, 'gi');
    let found = false;
    
    for (let i = textNodes.length - 1; i >= 0; i--) {
      const textNode = textNodes[i];
      const text = textNode.textContent;
      
      regex.lastIndex = 0;
      if (regex.test(text)) {
        found = true;
        regex.lastIndex = 0;
        const highlightedText = text.replace(regex, '<mark class="search-highlight bg-yellow-300 text-gray-900 px-0.5 rounded">$1</mark>');
        
        const wrapper = document.createElement('span');
        wrapper.className = 'search-highlight-container';
        wrapper.innerHTML = highlightedText;
        
        if (textNode.parentNode) {
          textNode.parentNode.replaceChild(wrapper, textNode);
        }
      }
    }

    if (windowFound && !found) {
      // El texto existe pero no lo encontramos en nodos de texto directos
    } else if (found) {
      try {
        window.find(term, false, false, true, false, true, false);
      } catch (e) {
        // Ignorar errores
      }
    }
  }, [clearHighlights]);

  const handleMobileQuickSearch = useCallback(() => {
    if (typeof window === 'undefined') return;
    const term = mobileSearchTerm.trim();
    if (!term) {
      clearHighlights();
      return;
    }

    highlightText(term);
  }, [mobileSearchTerm, highlightText, clearHighlights]);

  const loadTrips = async () => {
    try {
      const data = await apiService.trip.getAll();
      setTrips(data);
    } catch (err) {
      setError('Error al cargar viajes: ' + err.message);
    }
  };

  const loadEnvivos = async (tripId = null) => {
    try {
      let data;
      if (tripId) {
        // Cargar solo los en vivos del viaje seleccionado
        data = await apiService.envivo.getByTrip(tripId);
      } else {
        // Si no hay viaje seleccionado, cargar todos
        data = await apiService.envivo.getAll();
      }
      // Validar que data sea un array
      setEnvivos(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Error al cargar en vivos: ' + err.message);
      setEnvivos([]);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Filtrar filtros vacíos
      const activeFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          // Convertir filtro de estado a rangos de porcentaje
          if (key === 'status') {
            switch (value) {
              case 'no_payment':
                acc.payment_percentage_min = 0;
                acc.payment_percentage_max = 0;
                break;
              case 'partial_payment':
                acc.payment_percentage_min = 0.01;
                acc.payment_percentage_max = 49.99;
                break;
              case 'complete_payment':
                acc.payment_percentage_min = 50;
                acc.payment_percentage_max = 99.99;
                break;
              case 'full_payment':
                acc.payment_percentage_min = 100;
                acc.payment_percentage_max = 100;
                break;
              default:
                acc[key] = value;
            }
          } else {
            acc[key] = value;
          }
        }
        return acc;
      }, {});

      const data = await apiService.search.getConsolidated(activeFilters);
      debugLog('🔍 Datos de búsqueda recibidos:', data.data || data);
      setResults(data.data || data);
    } catch (err) {
      setError('Error al buscar datos: ' + err.message);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [key]: value
      };
      // Si cambia el viaje seleccionado, limpiar el filtro de en vivo
      // porque los en vivos se recargarán automáticamente en el useEffect
      if (key === 'tripId') {
        newFilters.envivoId = '';
      }
      return newFilters;
    });
  };

  // Funciones para manejar el tooltip
  const handleMouseEnter = (event, productos) => {
    // Solo mostrar tooltip si hay productos y no hay uno ya abierto
    if (!productos || productos.length === 0 || tooltipData) return;
    
    // Limpiar timeout anterior si existe
    if (tooltipTimeout) {
      clearTimeout(tooltipTimeout);
      setTooltipTimeout(null);
    }
    
    // const rect = event.target.getBoundingClientRect();
    // setTooltipPosition({
    //   x: rect.left + rect.width / 2,
    //   y: rect.bottom + 10
    // });
    setTooltipData(productos);
  };

  const handleClick = (event, productos) => {
    // Alternativa de click para mostrar el tooltip
    if (!productos || productos.length === 0) return;
    
    event.stopPropagation();
    
    // Limpiar timeout anterior si existe
    if (tooltipTimeout) {
      clearTimeout(tooltipTimeout);
      setTooltipTimeout(null);
    }
    
    // const rect = event.target.getBoundingClientRect();
    // setTooltipPosition({
    //   x: rect.left + rect.width / 2,
    //   y: rect.bottom + 10
    // });
    setTooltipData(productos);
  };

  const handleMouseLeave = (event) => {
    // Solo ocultar si el tooltip NO está visible
    // Si el tooltip ya está abierto, no cerrarlo automáticamente
    if (!tooltipData) {
      const relatedTarget = event.relatedTarget;
      const currentTarget = event.currentTarget;
      
      // Verificar si el mouse se movió a un elemento hijo o relacionado
      if (!relatedTarget || !currentTarget.contains(relatedTarget)) {
        // Agregar un pequeño delay para evitar parpadeos
        const timeout = setTimeout(() => {
          setTooltipData(null);
        }, 100);
        setTooltipTimeout(timeout);
      }
    }
  };

  const handleTouchStart = (event, productos) => {
    event.preventDefault();
    setTooltipData(productos);
  };

  const handleCloseTooltip = () => {
    // Limpiar timeout si existe
    if (tooltipTimeout) {
      clearTimeout(tooltipTimeout);
      setTooltipTimeout(null);
    }
    setTooltipData(null);
  };

  // Funciones para manejar el tooltip de pagos
  const handlePaymentMouseEnter = (event, payments) => {
    // Solo mostrar tooltip si hay pagos y no hay uno ya abierto
    if (!payments || payments.length === 0 || paymentTooltipData) return;
    
    // Limpiar timeout anterior si existe
    if (paymentTooltipTimeout) {
      clearTimeout(paymentTooltipTimeout);
      setPaymentTooltipTimeout(null);
    }
    
    // const rect = event.target.getBoundingClientRect();
    // setPaymentTooltipPosition({
    //   x: rect.left + rect.width / 2,
    //   y: rect.bottom + 10
    // });
    setPaymentTooltipData(payments);
  };

  const handlePaymentMouseLeave = (event) => {
    // Solo ocultar si el tooltip NO está visible
    if (!paymentTooltipData) {
      const relatedTarget = event.relatedTarget;
      const currentTarget = event.currentTarget;
      
      // Validar que ambos sean Nodes válidos antes de usar contains
      if (!relatedTarget || !currentTarget) {
        const timeout = setTimeout(() => {
          setPaymentTooltipData(null);
        }, 100);
        setPaymentTooltipTimeout(timeout);
        return;
      }
      
      // Verificar que currentTarget es un Node y que relatedTarget es un Node
      if (currentTarget instanceof Node && relatedTarget instanceof Node) {
        if (!currentTarget.contains(relatedTarget)) {
          // Agregar un pequeño delay para evitar parpadeos
          const timeout = setTimeout(() => {
            setPaymentTooltipData(null);
          }, 100);
          setPaymentTooltipTimeout(timeout);
        }
      } else {
        // Si no son Nodes válidos, ocultar directamente
        const timeout = setTimeout(() => {
          setPaymentTooltipData(null);
        }, 100);
        setPaymentTooltipTimeout(timeout);
      }
    }
  };

  const handlePaymentClick = (event, payments) => {
    // Alternativa de click para mostrar el tooltip de pagos
    if (!payments || payments.length === 0) return;
    
    event.stopPropagation();
    
    // Limpiar timeout anterior si existe
    if (paymentTooltipTimeout) {
      clearTimeout(paymentTooltipTimeout);
      setPaymentTooltipTimeout(null);
    }
    
    // const rect = event.target.getBoundingClientRect();
    // setPaymentTooltipPosition({
    //   x: rect.left + rect.width / 2,
    //   y: rect.bottom + 10
    // });
    setPaymentTooltipData(payments);
  };

  const handleClosePaymentTooltip = () => {
    // Limpiar timeout si existe
    if (paymentTooltipTimeout) {
      clearTimeout(paymentTooltipTimeout);
      setPaymentTooltipTimeout(null);
    }
    setPaymentTooltipData(null);
  };

  const getSortedResults = () => {
    if (!sortConfig.key) return results;

    return [...results].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Manejar valores numéricos
      if (sortConfig.key === 'total_orders' || sortConfig.key === 'payment_percentage') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      // Manejar valores monetarios
      if (sortConfig.key === 'total_ordered' || sortConfig.key === 'total_paid') {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      // Manejar strings
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Función para toggle del acordeón
  const toggleAccordion = (accordionKey) => {
    setOpenAccordions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accordionKey)) {
        newSet.delete(accordionKey);
      } else {
        newSet.add(accordionKey);
      }
      return newSet;
    });
  };

  // Agrupar resultados por usuario y viaje
  const groupedResults = useMemo(() => {
    const sortedResults = getSortedResults();
    const groups = {};

    sortedResults.forEach(row => {
      // Crear una clave única combinando username y trip_id
      const key = `${row.username}-${row.trip_id}`;

      if (!groups[key]) {
        groups[key] = {
          username: row.username,
          tripId: row.trip_id,
          tripName: row.trip_name,
          envivos: [],
          // Inicializar totales consolidados
          totalOrdered: 0,
          totalPaid: 0,
          totalPending: 0
        };
      }

      // Agregar en vivo al grupo
      groups[key].envivos.push(row);

      // Sumar totales consolidados
      groups[key].totalOrdered += parseFloat(row.total_ordered || 0);
      groups[key].totalPaid += parseFloat(row.total_paid || 0);
      groups[key].totalPending += parseFloat(row.total_ordered || 0) - parseFloat(row.total_paid || 0);
    });

    return Object.values(groups);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, sortConfig]);

  const clearFilters = () => {
    setFilters({
      tripId: '',
      clientName: '',
      envivoId: '',
      status: '',
      startDate: '',
      endDate: ''
    });
    setResults([]);
  };

  const exportToExcel = () => {
    if (results.length === 0) return;

    // Crear workbook de Excel
    const workbook = XLSX.utils.book_new();

    // Preparar datos para Excel
    const excelData = results.map(row => ({
      'Username Instagram': row.username,
      'Viaje': row.trip_name,
      'En Vivo': row.envivo_name,
      'Total Pedidos': row.total_orders || 0,
      'Total Ordenado': row.total_ordered,
      'Total Pagado': row.total_paid,
      '% Pago': row.payment_percentage,
      'Estado': row.order_statuses?.join(';') || ''
    }));

    // Crear hoja de cálculo
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Ajustar ancho de columnas
    const columnWidths = [
      { wch: 18 }, // Username Instagram
      { wch: 25 }, // Viaje
      { wch: 25 }, // En Vivo
      { wch: 12 }, // Total Pedidos
      { wch: 15 }, // Total Ordenado
      { wch: 15 }, // Total Pagado
      { wch: 10 }, // % Pago
      { wch: 15 }  // Estado
    ];
    worksheet['!cols'] = columnWidths;

    // Agregar hoja al workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Consolidado de Búsqueda');

    // Generar y descargar archivo
    const fileName = `consolidado_busqueda_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  // Función para ver comprobante de pago con autenticación
  const handleViewReceipt = async (receiptUrl, event) => {
    if (event) {
      event.stopPropagation(); // Evitar que se abra/cierre el acordeón
      event.preventDefault(); // Evitar comportamiento por defecto
    }

    if (!receiptUrl) {
      setReceiptError('No hay comprobante disponible');
      return;
    }

    try {
      setReceiptLoading(true);
      setReceiptError(null);
      setShowReceiptModal(true);
      setSelectedReceiptUrl(receiptUrl);

      // Obtener token del localStorage
      const token = localStorage.getItem('makana_token');

      if (!token) {
        throw new Error('No hay sesión activa');
      }

      // Hacer fetch con el token en el header Authorization
      const response = await fetch(receiptUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Error al cargar el comprobante: ${response.status}`);
      }

      // Convertir respuesta a blob
      const blob = await response.blob();

      // Crear URL del blob para mostrar la imagen en el modal
      const imageUrl = URL.createObjectURL(blob);

      setReceiptImageSrc(imageUrl);
      setReceiptLoading(false);

    } catch (err) {
      errorLog('Error cargando imagen:', err);
      setReceiptError(err.message || 'Error al cargar el comprobante');
      setReceiptLoading(false);
    }
  };

  // Función para cerrar modal de comprobante
  const handleCloseReceiptModal = () => {
    // Liberar URL del blob
    if (receiptImageSrc) {
      URL.revokeObjectURL(receiptImageSrc);
    }
    setShowReceiptModal(false);
    setSelectedReceiptUrl(null);
    setReceiptImageSrc(null);
    setReceiptError(null);
    setReceiptLoading(false);
  };

  // Manejo de quick_checked (revisión rápida) solo en esta tabla de búsqueda
  const handleQuickCheckToggle = async (row) => {
    // En búsqueda consolidada, cada fila representa un cliente con múltiples órdenes
    // Necesitamos obtener todas las órdenes de ese cliente/envivo y actualizar quick_checked en todas
    
    // Intentar primero si hay order_id directo (por si el backend lo incluye)
    const directOrderId = row.order_id || row.id;
    
    // Si no hay order_id directo, necesitamos obtener las órdenes del cliente/envivo
    if (!directOrderId && (!row.username || !row.envivo_id)) {
      errorLog('No se encontró información suficiente para actualizar quick_checked', row);
      setError('Error: No se puede identificar la orden para actualizar');
      return;
    }

    const currentValue = row.quick_checked === 1 ? 1 : 0;
    const newValue = currentValue === 1 ? 0 : 1;

    // Identificador único para esta fila (cliente + envivo)
    const rowId = `${row.username}-${row.envivo_id}`;
    setQuickCheckUpdatingId(rowId);

    // Actualización optimista en la UI
    setResults((prev) =>
      prev.map((item) =>
        item.username === row.username && item.envivo_id === row.envivo_id
          ? { ...item, quick_checked: newValue }
          : item
      )
    );

    try {
      let orderIdsToUpdate = [];
      
      // Si hay order_id directo, usarlo
      if (directOrderId) {
        orderIdsToUpdate = [directOrderId];
      } else {
        // Obtener todas las órdenes del cliente en ese envivo
        const allOrders = await apiService.order.getByEnvivo(row.envivo_id);
        // Filtrar por cliente (username)
        const clientOrders = allOrders.filter(order => 
          order.client_username === row.username || 
          order.client?.username === row.username
        );
        orderIdsToUpdate = clientOrders.map(order => order.id).filter(Boolean);
      }

      if (orderIdsToUpdate.length === 0) {
        throw new Error('No se encontraron órdenes para actualizar');
      }

      // Actualizar quick_checked en todas las órdenes encontradas
      const updatePromises = orderIdsToUpdate.map(orderId =>
        apiService.order.updateQuickChecked(orderId, !!newValue)
      );
      
      await Promise.all(updatePromises);

      // Actualizar el resultado con el valor del backend
      setResults((prev) =>
        prev.map((item) =>
          item.username === row.username && item.envivo_id === row.envivo_id
            ? { ...item, quick_checked: newValue }
            : item
        )
      );
      setError('');
    } catch (err) {
      errorLog('Error actualizando quick_checked:', err);
      setError('Error al actualizar la revisión rápida: ' + err.message);
      // Revertir al valor anterior
      setResults((prev) =>
        prev.map((item) =>
          item.username === row.username && item.envivo_id === row.envivo_id
            ? { ...item, quick_checked: currentValue }
            : item
        )
      );
    } finally {
      setQuickCheckUpdatingId(null);
    }
  };

  // Funciones auxiliares para calcular min_payment
  /**
   * Calcula carry_on_extra a partir de los items (no usado actualmente)
   * @param {Array} items - Items de la orden
   * @returns {number} Suma de precios de items marcados como maleta
   */
  // const calculateCarryOnExtra = (items) => {
  //   if (!items || !Array.isArray(items)) return 0;
  //   return items.reduce((sum, item) => {
  //     if (item.carry_on === 1) {
  //       return sum + (parseFloat(item.price || 0) * (item.quantity || 1));
  //     }
  //     return sum;
  //   }, 0);
  // };

  /**
   * Calcula el abono mínimo de una orden
   * @param {number} totalAmount - Total de la orden
   * @param {number} carryOnExtra - Suma de precios de items marcados como maleta
   * @returns {number} Abono mínimo requerido
   */
  const calculateMinPayment = (totalAmount, carryOnExtra = 0) => {
    return 0.5 * (carryOnExtra + totalAmount);
  };

  /**
   * Obtiene el min_payment de un resultado (del backend o calculado)
   * @param {Object} row - Fila de resultado de búsqueda
   * @returns {number} Abono mínimo requerido
   */
  const getMinPaymentForRow = (row) => {
    // Si el backend ya calculó min_payment, usarlo
    if (row.min_payment !== undefined && row.min_payment !== null) {
      return parseFloat(row.min_payment || 0);
    }
    
    // Si hay carry_on_extra, usar la fórmula
    if (row.carry_on_extra !== undefined && row.carry_on_extra !== null) {
      const totalAmount = parseFloat(row.total_ordered || 0);
      const carryOnExtra = parseFloat(row.carry_on_extra || 0);
      return calculateMinPayment(totalAmount, carryOnExtra);
    }
    
    // Fallback: calcular desde 0 (sin items en maleta) = 50% del total
    const totalAmount = parseFloat(row.total_ordered || 0);
    return calculateMinPayment(totalAmount, 0);
  };

  /**
   * Calcula el percentage basado en min_payment (no usado actualmente)
   * @param {number} totalPaid - Total pagado
   * @param {number} minPayment - Abono mínimo requerido
   * @param {number} totalOrdered - Total de la orden
   * @returns {number} Porcentaje del min_payment (0-100+)
   */
  // const calculatePaymentPercentageOfMin = (totalPaid, minPayment, totalOrdered) => {
  //   if (minPayment <= 0) return totalOrdered > 0 ? (totalPaid / totalOrdered) * 100 : 0;
  //   return (totalPaid / minPayment) * 100;
  // };

  const getStatusColor = (percentage, minPaymentPercent = null) => {
    if (percentage === 100) return 'text-green-700 bg-green-200';  // Verde oscuro para Pago Completo
    // Si minPaymentPercent está disponible, usar ese umbral en lugar de 50%
    const threshold = minPaymentPercent !== null && minPaymentPercent !== undefined ? 100 : 50;
    if (percentage >= threshold) return 'text-blue-600 bg-blue-100';      // Azul para Abono Completo
    if (percentage > 0) return 'text-yellow-600 bg-yellow-100';    // Amarillo para Abono Parcial
    return 'text-red-600 bg-red-100';                             // Rojo para Sin Pago
  };

  const getStatusText = (percentage, minPaymentPercent = null) => {
    if (percentage === 100) return 'Pago Completo';
    // Si minPaymentPercent está disponible, usar ese umbral en lugar de 50%
    const threshold = minPaymentPercent !== null && minPaymentPercent !== undefined ? 100 : 50;
    if (percentage >= threshold) return 'Abono Completo';
    if (percentage > 0) return 'Abono Parcial';
    return 'Sin Pago';
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
        <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 xl:px-12 py-6">
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center gap-3">
            <Search className="text-blue-600" size={32} />
            Módulo de Búsqueda y Consolidado
          </h1>
          <p className="text-gray-600">
            Busca y consolida información de clientes, pedidos y en vivos
          </p>
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Filter className="text-purple-600" size={24} />
            <h2 className="text-xl font-semibold text-gray-800">Filtros de Búsqueda</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Filtro por Viaje */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Viaje
              </label>
              <select
                value={filters.tripId}
                onChange={(e) => handleFilterChange('tripId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos los viajes</option>
                {trips.map(trip => (
                  <option key={trip.id} value={trip.id}>
                    {trip.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro por Cliente */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cliente (Nombre/Username)
              </label>
              <input
                type="text"
                value={filters.clientName}
                onChange={(e) => handleFilterChange('clientName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nombre o username de Instagram"
              />
            </div>

            {/* Filtro por En Vivo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                En Vivo
              </label>
              <select
                value={filters.envivoId}
                onChange={(e) => handleFilterChange('envivoId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos los en vivos</option>
                {envivos.map(envivo => (
                  <option key={envivo.id} value={envivo.id}>
                    {envivo.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtro por Estado */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estado de Pago
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos los estados</option>
                <option value="no_payment">Sin Pago</option>
                <option value="partial_payment">Abono Parcial</option>
                <option value="complete_payment">Abono Completo</option>
                <option value="full_payment">Pago Completo</option>
              </select>
            </div>

            {/* Filtro por Fecha Inicio */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Inicio
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Filtro por Fecha Fin */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Fin
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Botones de Acción */}
          <div className="flex justify-between items-center mt-6">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Limpiar Filtros
            </button>
            
            <div className="flex gap-3">
              <button
                onClick={exportToExcel}
                disabled={results.length === 0}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                <Download size={16} />
                Exportar Excel
              </button>
              
              <button
                onClick={handleSearch}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                <Search size={16} />
                {loading ? 'Buscando...' : 'Buscar'}
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {/* Resultados */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
              <Users className="text-purple-600" size={24} />
              Resultados de la Búsqueda
            </h2>
            {results.length > 0 && (
              <span className="text-sm text-gray-600">
                {results.length} resultado{results.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Buscando datos...</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-12">
              <Search size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No hay resultados</h3>
              <p className="text-gray-500">
                Ajusta los filtros y vuelve a buscar para encontrar información
              </p>
            </div>
          ) : (
            <div>
              {/* Info de filtros aplicados */}
              <div className="mb-4 pb-4 border-b border-gray-200">
                <div className="flex flex-wrap items-center gap-3">
                  {(() => {
                    const trip = trips.find(t => String(t.id) === String(filters.tripId));
                    const env = envivos.find(e => String(e.id) === String(filters.envivoId));
                    return (
                      <>
                        {trip && (
                          <span className="inline-flex items-center gap-2 text-sm font-medium text-blue-800 bg-blue-50 border border-blue-200 rounded-full px-3 py-1">
                            <MapPin size={16} className="text-blue-600" />
                            <span>Viaje: {trip.name}</span>
                          </span>
                        )}
                        {env && (
                          <span className="inline-flex items-center gap-2 text-sm font-medium text-purple-800 bg-purple-50 border border-purple-200 rounded-full px-3 py-1">
                            <ShoppingCart size={16} className="text-purple-600" />
                            <span>En vivo: {env.name}</span>
                          </span>
                        )}
                        {!trip && !env && (
                          <span className="text-sm text-gray-600">Sin filtro de viaje/en vivo aplicado</span>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Acordeones de resultados agrupados por usuario y viaje */}
              <div className="space-y-3">
                {groupedResults.map((group, groupIndex) => {
                  const accordionKey = `${group.username}-${group.tripId}`;
                  const isOpen = openAccordions.has(accordionKey);

                  return (
                    <div key={accordionKey} className="border border-gray-200 rounded-lg overflow-hidden">
                      {/* Header del acordeón */}
                      <div className="bg-gradient-to-r from-blue-50 to-purple-50">
                        <button
                          onClick={() => toggleAccordion(accordionKey)}
                          className="w-full p-4 pb-2 hover:from-blue-100 hover:to-purple-100 transition-colors duration-200 text-left"
                        >
                          {/* Título principal */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <Users size={20} className="text-blue-600 flex-shrink-0" />
                              <div className="text-left">
                                <div className="font-semibold text-gray-800">
                                  <span className="text-blue-600">@{group.username}</span>
                                  <span className="mx-2 text-gray-400">•</span>
                                  <span className="text-purple-600">{group.tripName}</span>
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  {group.envivos.length} en vivo{group.envivos.length !== 1 ? 's' : ''}
                                </div>
                              </div>
                            </div>
                            <ChevronDown
                              className={`w-5 h-5 text-gray-600 transition-transform duration-200 flex-shrink-0 ${
                                isOpen ? 'rotate-180' : ''
                              }`}
                            />
                          </div>

                          {/* Resumen financiero */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            {/* Total Ordenado */}
                            <div className="bg-white/60 rounded-lg p-2 text-left">
                              <div className="text-xs text-gray-600 font-medium">Total Ordenado</div>
                              <div className="text-sm font-bold text-gray-800">
                                ${group.totalOrdered.toLocaleString('es-CO')}
                              </div>
                            </div>

                            {/* Total Pagado */}
                            <div className="bg-white/60 rounded-lg p-2 text-left">
                              <div className="text-xs text-gray-600 font-medium">Total Pagado</div>
                              <div className="text-sm font-bold text-green-600">
                                ${group.totalPaid.toLocaleString('es-CO')}
                              </div>
                            </div>

                            {/* Total Pendiente */}
                            <div className="bg-white/60 rounded-lg p-2 text-left">
                              <div className="text-xs text-gray-600 font-medium">Saldo Pendiente</div>
                              <div className={`text-sm font-bold ${
                                group.totalPending <= 0 ? 'text-green-600' :
                                group.totalPending < group.totalOrdered / 2 ? 'text-yellow-600' :
                                'text-red-600'
                              }`}>
                                ${group.totalPending.toLocaleString('es-CO')}
                              </div>
                            </div>

                            {/* Estado de Pago */}
                            <div className="bg-white/60 rounded-lg p-2 text-left flex items-center justify-center">
                              {(() => {
                                const percentage = group.totalOrdered > 0
                                  ? (group.totalPaid / group.totalOrdered) * 100
                                  : 0;

                                let statusText = 'Sin Pago';
                                let statusColor = 'bg-red-100 text-red-700 border border-red-300';

                                if (percentage >= 100) {
                                  statusText = 'Pago Completo';
                                  statusColor = 'bg-green-100 text-green-700 border border-green-300';
                                } else if (percentage >= 50) {
                                  statusText = 'Abono Completo';
                                  statusColor = 'bg-blue-100 text-blue-700 border border-blue-300';
                                } else if (percentage > 0) {
                                  statusText = 'Abono Parcial';
                                  statusColor = 'bg-yellow-100 text-yellow-700 border border-yellow-300';
                                }

                                return (
                                  <div className="flex flex-col items-center gap-1 w-full">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>
                                      {statusText}
                                    </span>
                                    <span className="text-xs font-semibold text-gray-700">
                                      {percentage.toFixed(0)}%
                                    </span>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </button>

                        {/* Pagos inline - FUERA del button para evitar buttons anidados */}
                        {(() => {
                          // Consolidar todos los pagos de los en vivos del grupo
                          const paymentsWithDuplicates = group.envivos.flatMap(envivo => {
                            const tripAccount = (envivo.trips_accounts || []).find(
                              ta => ta.trip_id === group.tripId
                            );
                            return tripAccount?.payments || [];
                          });

                          // Deduplicar pagos por payment_id
                          const uniquePaymentsMap = new Map();
                          paymentsWithDuplicates.forEach(payment => {
                            if (payment.payment_id && !uniquePaymentsMap.has(payment.payment_id)) {
                              uniquePaymentsMap.set(payment.payment_id, payment);
                            }
                          });
                          const allPayments = Array.from(uniquePaymentsMap.values());

                          if (allPayments.length === 0) {
                            return null;
                          }

                          return (
                            <div className="px-4 pb-3">
                              <div className="flex items-center gap-1 overflow-x-auto">
                                {allPayments.map((payment, idx) => (
                                  <div
                                    key={`payment-${payment.payment_id}-${idx}`}
                                    className="bg-white/80 rounded px-1.5 py-1 flex items-center gap-1 flex-shrink-0"
                                    style={{ maxWidth: '120px' }}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="text-[9px] text-gray-700 truncate font-medium">
                                        {payment.bank_name}
                                      </div>
                                      <div className="text-[10px] text-green-600 font-semibold truncate">
                                        ${parseFloat(payment.amount || payment.total || 0).toLocaleString('es-CO')}
                                      </div>
                                    </div>
                                    {payment.receipt_url && (
                                      <button
                                        onClick={(e) => handleViewReceipt(payment.receipt_url, e)}
                                        className="p-0.5 bg-blue-100 hover:bg-blue-200 rounded text-blue-600 transition-colors flex-shrink-0"
                                        title="Ver comprobante"
                                      >
                                        <ImageIcon size={10} />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>

                      {/* Contenido del acordeón (en vivos) */}
                      {isOpen && (
                        <div className="bg-white">
                          {group.envivos.map((row, envivoIndex) => (
                            <div
                              key={`${row.envivo_id}-${envivoIndex}`}
                              className="border-t border-gray-100 p-4 hover:bg-gray-50 transition-colors"
                            >
                              {/* Nombre del En Vivo */}
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <ShoppingCart size={16} className="text-purple-600" />
                                  <span className="font-medium text-gray-800">{row.envivo_name}</span>
                                </div>
                                <div
                                  className="relative cursor-pointer"
                                  onMouseEnter={(e) => handleMouseEnter(e, row.productos || [])}
                                  onMouseLeave={handleMouseLeave}
                                  onClick={(e) => handleClick(e, row.productos || [])}
                                  onTouchStart={(e) => handleTouchStart(e, row.productos || [])}
                                  title="Ver productos del en vivo"
                                >
                                  <ShoppingCart
                                    size={18}
                                    className="text-gray-600 hover:text-blue-600 transition-colors"
                                  />
                                  {row.productos && row.productos.length > 0 && (
                                    <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                                      {row.productos.length}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Información financiera en grid */}
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                                <div className="bg-gray-50 rounded p-2">
                                  <div className="text-xs text-gray-600">Total Ordenado</div>
                                  <div className="font-semibold text-gray-800">
                                    ${parseFloat(row.total_ordered || 0).toLocaleString('es-CO')}
                                  </div>
                                </div>
                                <div
                                  className="bg-gray-50 rounded p-2 cursor-pointer hover:bg-gray-100"
                                  onMouseEnter={(e) => handlePaymentMouseEnter(e, row.payments || [])}
                                  onMouseLeave={handlePaymentMouseLeave}
                                  onClick={(e) => handlePaymentClick(e, row.payments || [])}
                                  title={row.payments && row.payments.length > 0 ? `Ver detalles de ${row.payments.length} pago(s)` : 'Sin pagos registrados'}
                                >
                                  <div className="text-xs text-gray-600">Total Pagado</div>
                                  <div className="font-semibold text-green-600">
                                    ${parseFloat(row.total_paid || 0).toLocaleString('es-CO')}
                                  </div>
                                </div>
                                <div className="bg-gray-50 rounded p-2">
                                  <div className="text-xs text-gray-600">% Pago</div>
                                  <div className="font-semibold text-gray-800">
                                    {row.payment_percentage || 0}%
                                  </div>
                                </div>
                                <div className="bg-gray-50 rounded p-2 flex items-center justify-center">
                                  {(() => {
                                    const totalPaid = parseFloat(row.total_paid || 0);
                                    const totalOrdered = parseFloat(row.total_ordered || 0);
                                    const minPayment = getMinPaymentForRow(row);
                                    const paymentPercentage = totalOrdered > 0 ? (totalPaid / totalOrdered) * 100 : 0;
                                    const minPaymentPercent = minPayment > 0 ? (totalPaid / minPayment) * 100 : 0;

                                    return (
                                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(paymentPercentage, minPaymentPercent >= 100 ? 100 : null)}`}>
                                        {getStatusText(paymentPercentage, minPaymentPercent >= 100 ? 100 : null)}
                                      </span>
                                    );
                                  })()}
                                </div>
                              </div>

                              {/* Quick Check */}
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Quick Check:</span>
                                {(() => {
                                  const isChecked = row.quick_checked === 1;
                                  const rowId = `${row.username}-${row.envivo_id}`;
                                  const isUpdating = quickCheckUpdatingId === rowId;
                                  return (
                                    <button
                                      type="button"
                                      onClick={() => !isUpdating && handleQuickCheckToggle(row)}
                                      disabled={isUpdating}
                                      className={`inline-flex items-center justify-center w-20 h-8 rounded-full border-2 transition-colors px-3 ${
                                        isChecked
                                          ? 'bg-green-100 border-green-500 text-green-700'
                                          : 'bg-transparent border-gray-300 text-gray-400'
                                      } ${isUpdating ? 'opacity-60 cursor-wait' : 'hover:border-green-400 hover:text-green-600'}`}
                                      title={
                                        isChecked
                                          ? 'Orden revisada rápidamente'
                                          : 'Marcar como revisada rápidamente'
                                      }
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        readOnly
                                        className="pointer-events-none accent-green-600 w-4 h-4"
                                      />
                                    </button>
                                  );
                                })()}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tooltip para mostrar productos */}
      {tooltipData && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={handleCloseTooltip}
        >
          <div 
            className="bg-white border border-gray-300 rounded-lg shadow-lg p-4 w-full max-w-sm max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header con botón de cerrar */}
            <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-200">
              <div className="text-sm font-semibold text-gray-800">
                Productos ({tooltipData.length})
              </div>
              <button
                onClick={handleCloseTooltip}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={18} className="text-gray-500" />
              </button>
            </div>
            
            {/* Lista de productos */}
            <div className="max-h-60 overflow-y-auto space-y-2">
              {tooltipData.map((producto, index) => (
                <div key={index} className="border-b border-gray-100 pb-2 last:border-b-0">
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 text-sm">
                        {producto.product_name}
                      </div>
                    </div>
                    <div className="text-center mx-2">
                      <div className="text-xs text-gray-600">
                        x{producto.quantity}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-green-600 text-sm">
                        ${parseFloat(producto.price || 0).toLocaleString('es-CO')}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tooltip para mostrar pagos */}
      {paymentTooltipData && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={handleClosePaymentTooltip}
        >
          <div 
            className="bg-white border border-gray-300 rounded-lg shadow-lg p-4 w-full max-w-sm max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header con botón de cerrar */}
            <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-200">
              <div className="text-sm font-semibold text-gray-800">
                Pagos Realizados ({paymentTooltipData.length})
              </div>
              <button
                onClick={handleClosePaymentTooltip}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={18} className="text-gray-500" />
              </button>
            </div>
            
            {/* Lista de pagos */}
            <div className="max-h-60 overflow-y-auto space-y-2">
              {paymentTooltipData.map((payment, index) => (
                <div key={index} className="border-b border-gray-100 pb-2 last:border-b-0">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 text-sm">
                        Pago #{index + 1}
                      </div>
                      <div className="text-xs text-gray-600">
                        Banco: {payment.bank || 'No especificado'}
                      </div>
                      <div className="text-xs text-gray-500">
                        Fecha: {payment.created_at ? new Date(payment.created_at).toLocaleDateString('es-CO') : 'No disponible'}
                      </div>
                    </div>
                    <div className="text-right ml-2">
                      <div className="font-semibold text-green-600 text-sm">
                        ${parseFloat(payment.amount || 0).toLocaleString('es-CO')}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Total de pagos */}
            <div className="mt-3 pt-2 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-800 text-sm">Total Pagado:</span>
                <span className="font-bold text-green-600 text-sm">
                  ${paymentTooltipData.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0).toLocaleString('es-CO')}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botón "Ir Arriba" de la derecha (solo escritorio lg) */}
      <button
        onClick={() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        className="hidden lg:flex fixed bottom-8 right-8 bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 z-50 items-center justify-center hover:scale-110"
        title="Volver arriba"
      >
        <ArrowUp size={24} />
      </button>

      {/* Buscador y botón "Ir Arriba" para iPad y Mobile (md hasta lg) */}
      <div className={`md:flex lg:hidden fixed bottom-4 right-4 z-50 flex items-center gap-3 transition-all duration-300 ${
        showMobileSearch ? 'translate-y-0 opacity-100' : 'translate-y-[200%] opacity-0 pointer-events-none'
      }`}>
        {/* Campo de búsqueda expandible (se expande hacia la izquierda cuando se abre) */}
        <div className={`flex items-center bg-white shadow-lg rounded-full border border-gray-200 overflow-hidden transition-all duration-300 ${
          isSearchExpanded ? 'w-64 opacity-100' : 'w-0 opacity-0 overflow-hidden'
        }`}>
          {/* Botón X para cerrar (a la izquierda del campo) */}
          {isSearchExpanded && (
            <button
              type="button"
              onClick={() => {
                setIsSearchExpanded(false);
                setMobileSearchTerm('');
                // Limpiar resaltados
                const existingHighlights = document.querySelectorAll('.search-highlight');
                existingHighlights.forEach(el => {
                  const parent = el.parentNode;
                  if (parent && parent.className === 'search-highlight-container') {
                    parent.replaceWith(document.createTextNode(el.textContent));
                  }
                });
                document.body.normalize();
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
              title="Cerrar búsqueda"
            >
              <X size={20} className="font-bold" strokeWidth={3} />
            </button>
          )}
          <input
            type="text"
            value={mobileSearchTerm}
            onChange={(event) => {
              setMobileSearchTerm(event.target.value);
              // Si el campo se limpia, eliminar resaltados
              if (!event.target.value.trim()) {
                const existingHighlights = document.querySelectorAll('.search-highlight');
                existingHighlights.forEach(el => {
                  const parent = el.parentNode;
                  if (parent && parent.className === 'search-highlight-container') {
                    parent.replaceWith(document.createTextNode(el.textContent));
                  }
                });
                document.body.normalize();
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleMobileQuickSearch();
              }
              if (event.key === 'Escape') {
                setIsSearchExpanded(false);
                setMobileSearchTerm('');
                // Limpiar resaltados
                const existingHighlights = document.querySelectorAll('.search-highlight');
                existingHighlights.forEach(el => {
                  const parent = el.parentNode;
                  if (parent && parent.className === 'search-highlight-container') {
                    parent.replaceWith(document.createTextNode(el.textContent));
                  }
                });
                document.body.normalize();
              }
            }}
            onBlur={() => {
              // Cerrar si está vacío al perder el foco
              if (!mobileSearchTerm.trim()) {
                setIsSearchExpanded(false);
                // Limpiar resaltados
                const existingHighlights = document.querySelectorAll('.search-highlight');
                existingHighlights.forEach(el => {
                  const parent = el.parentNode;
                  if (parent && parent.className === 'search-highlight-container') {
                    parent.replaceWith(document.createTextNode(el.textContent));
                  }
                });
                document.body.normalize();
              }
            }}
            placeholder="Buscar en la página..."
            className={`px-4 py-3 text-sm text-gray-700 outline-none transition-all ${
              isSearchExpanded ? 'w-full flex-1' : 'w-0 hidden'
            }`}
            aria-label="Buscar en la página"
            autoFocus={isSearchExpanded}
          />
          {isSearchExpanded && (
            <button
              type="button"
              onClick={handleMobileQuickSearch}
              className="px-4 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center flex-shrink-0"
              title="Buscar coincidencia"
            >
              <Search size={20} />
            </button>
          )}
        </div>

        {/* Botón de lupa (solo visible cuando NO está expandido) */}
        <button
          type="button"
          onClick={() => setIsSearchExpanded(!isSearchExpanded)}
          className={`bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center hover:scale-110 z-50 ${
            isSearchExpanded ? 'hidden' : 'flex'
          }`}
          title="Buscar en la página"
        >
          <Search size={24} />
        </button>

        {/* Botón "Ir Arriba" */}
        <button
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center hover:scale-110 z-50"
          title="Volver arriba"
        >
          <ArrowUp size={24} />
        </button>
      </div>

      {/* Buscador completo para Mobile (menor a md) */}
      <div className={`md:hidden fixed bottom-4 right-4 left-4 z-50 flex flex-col items-center gap-3 transition-transform duration-300 ease-in-out ${
        showMobileSearch ? 'translate-y-0 opacity-100' : 'translate-y-[200%] opacity-0 pointer-events-none'
      }`}>
        <div className="flex items-center bg-white shadow-lg rounded-full border border-gray-200 overflow-hidden w-full max-w-sm mx-auto">
          <input
            type="text"
            value={mobileSearchTerm}
            onChange={(event) => {
              setMobileSearchTerm(event.target.value);
              // Si el campo se limpia, eliminar resaltados
              if (!event.target.value.trim()) {
                const existingHighlights = document.querySelectorAll('.search-highlight');
                existingHighlights.forEach(el => {
                  const parent = el.parentNode;
                  if (parent && parent.className === 'search-highlight-container') {
                    parent.replaceWith(document.createTextNode(el.textContent));
                  }
                });
                document.body.normalize();
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleMobileQuickSearch();
              }
            }}
            placeholder="Buscar en la página..."
            className="flex-1 px-4 py-3 text-sm text-gray-700 outline-none"
            aria-label="Buscar en la página"
          />
          <button
            type="button"
            onClick={handleMobileQuickSearch}
            className="px-4 py-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center"
            title="Buscar coincidencia"
          >
            <Search size={20} />
          </button>
        </div>

        {showScrollTop && (
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center hover:scale-110"
            title="Volver arriba"
          >
            <ArrowUp size={24} />
          </button>
        )}
        </div>
      </div>

      {/* Modal para ver comprobante de pago - FUERA del contenedor principal */}
      {showReceiptModal && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={handleCloseReceiptModal}
        >
          <div
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header del modal */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <ImageIcon size={20} className="text-blue-600" />
                Comprobante de Pago
              </h3>
              <button
                onClick={handleCloseReceiptModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                title="Cerrar"
              >
                <X size={20} className="text-gray-600" />
              </button>
            </div>

            {/* Contenido del modal */}
            <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-50">
              {receiptLoading && (
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Cargando comprobante...</p>
                </div>
              )}

              {receiptError && (
                <div className="text-center">
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    <p className="font-semibold">Error</p>
                    <p>{receiptError}</p>
                  </div>
                  <button
                    onClick={handleCloseReceiptModal}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              )}

              {!receiptLoading && !receiptError && receiptImageSrc && (
                <img
                  src={receiptImageSrc}
                  alt="Comprobante de pago"
                  className="max-w-full max-h-full object-contain rounded shadow-lg"
                />
              )}
            </div>

            {/* Footer con acciones */}
            {!receiptLoading && !receiptError && receiptImageSrc && (
              <div className="border-t border-gray-200 p-4 flex items-center justify-end gap-3">
                <button
                  onClick={handleCloseReceiptModal}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                >
                  Cerrar
                </button>
                {selectedReceiptUrl && (
                  <a
                    href={selectedReceiptUrl}
                    download
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                    onClick={(e) => {
                      e.preventDefault();
                      // Crear link temporal para descargar
                      const link = document.createElement('a');
                      link.href = receiptImageSrc;
                      link.download = `comprobante_${Date.now()}.jpg`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  >
                    <Download size={16} />
                    Descargar
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SearchModule;
